import { useMemo } from 'react';

import { ErdModel, ErdRelationship, HeuristicConfidence } from '../types';

interface HeuristicCandidate {
  sourceModelId: string;
  sourceColumn: string;
  baseName: string;
  targetModelId: string;
  targetColumn: string;
  confidence: HeuristicConfidence;
}

function inferCandidates(
  models: ErdModel[],
  existingEdgeSet: Set<string>,
): HeuristicCandidate[] {
  const modelByName = new Map<string, ErdModel>();
  const modelByNamePlural = new Map<string, ErdModel>();
  for (const model of models) {
    const shortName = model.name.toLowerCase();
    modelByName.set(shortName, model);
    if (shortName.endsWith('s')) {
      modelByNamePlural.set(shortName.slice(0, -1), model);
    }
  }

  const candidates: HeuristicCandidate[] = [];

  for (const model of models) {
    for (const column of model.columns) {
      const colName = column.name.toLowerCase();
      if (!colName.endsWith('_id')) continue;

      const baseName = colName.replace(/_id$/, '');
      if (!baseName) continue;

      const stripPrefix = (name: string) => {
        const parts = name.split('_');
        return parts.length > 1 ? parts.slice(1).join('_') : name;
      };

      const targetModel =
        modelByName.get(baseName) ??
        modelByNamePlural.get(baseName) ??
        modelByName.get(stripPrefix(baseName));

      if (!targetModel || targetModel.uniqueId === model.uniqueId) continue;

      const edgeKey = `${model.uniqueId}->${targetModel.uniqueId}`;
      if (existingEdgeSet.has(edgeKey)) continue;

      const hasUniqueTest = targetModel.tests.some(
        (t) =>
          t.testMetadataName === 'unique' &&
          (t.columnName === 'id' || t.columnName === colName),
      );

      candidates.push({
        sourceModelId: model.uniqueId,
        sourceColumn: column.name,
        baseName,
        targetModelId: targetModel.uniqueId,
        targetColumn: 'id',
        confidence: hasUniqueTest ? 'high' : 'medium',
      });
    }
  }

  return candidates;
}

export function useHeuristicInference(
  models: ErdModel[],
  existingEdges: ErdRelationship[],
): ErdRelationship[] {
  return useMemo(() => {
    const existingEdgeSet = new Set<string>();
    for (const edge of existingEdges) {
      existingEdgeSet.add(`${edge.sourceModelId}->${edge.targetModelId}`);
      existingEdgeSet.add(`${edge.targetModelId}->${edge.sourceModelId}`);
    }

    const candidates = inferCandidates(models, existingEdgeSet);

    return candidates.map((c) => ({
      id: `heuristic-${c.sourceModelId}-${c.sourceColumn}-${c.targetModelId}`,
      sourceModelId: c.sourceModelId,
      targetModelId: c.targetModelId,
      sourceColumn: c.sourceColumn,
      targetColumn: c.targetColumn,
      label: c.sourceColumn,
      edgeSource: 'heuristicInferred' as const,
      confidence: c.confidence,
      approvalStatus: 'pending' as const,
      hasUniqueTest: c.confidence === 'high',
    }));
  }, [models, existingEdges]);
}
